import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { SearchBar } from "../components/SearchBar";
import api, { API } from "../lib/api";
import { useSiteViewer } from "../context/ViewerContext";
import { MainMenu } from "../components/MainMenu";
import { AppsMenu } from "../components/AppsMenu";
import { Sparkles, ExternalLink, PlayCircle, Newspaper, Image as ImageIcon, Globe, Loader2 } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";

const TABS = [
  { id: "web", label: "Tout", icon: Globe },
  { id: "images", label: "Images", icon: ImageIcon },
  { id: "videos", label: "Vidéos", icon: PlayCircle },
  { id: "news", label: "News", icon: Newspaper },
];

function getHost(u) {
  try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; }
}

function ResultLinkOut({ href, title, children, className = "" }) {
  const { open } = useSiteViewer();
  const onClick = (e) => {
    // Allow middle-click / ctrl+click / cmd+click to open new tab natively
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    open(href, title);
  };
  return (
    <a
      href={href}
      onClick={onClick}
      onAuxClick={onClick}
      target="_blank"
      rel="noopener noreferrer"
      className={`omni-result-link ${className}`}
    >
      {children}
    </a>
  );
}

function WebResult({ r, i }) {
  const url = r.href || r.url || r.link || "#";
  const title = r.title || url;
  const body = r.body || r.description || "";
  return (
    <li className="omni-fade-in" style={{ animationDelay: `${i * 30}ms` }} data-testid={`web-result-${i}`}>
      <ResultLinkOut href={url} title={title} className="block">
        <div className="text-xs text-gray-500 flex items-center gap-1.5">
          <img
            src={`https://www.google.com/s2/favicons?domain=${getHost(url)}&sz=32`}
            alt=""
            className="w-4 h-4 rounded-sm"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <span className="truncate">{getHost(url)}</span>
        </div>
        <h3 className="result-title text-xl mt-1 text-[#1a0dab] font-medium leading-snug">{title}</h3>
        {body && <p className="text-sm text-gray-700 leading-relaxed mt-1">{body}</p>}
      </ResultLinkOut>
    </li>
  );
}

function ImageResult({ r, i }) {
  const img = r.image || r.thumbnail || r.url;
  const src = r.url || r.image;
  return (
    <ResultLinkOut href={src} title={r.title} key={i} className="block">
      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-[var(--omni-border)] hover:border-black transition" data-testid={`image-result-${i}`}>
        <img src={img} alt={r.title || ""} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <p className="text-xs text-gray-600 mt-1 truncate">{r.title || getHost(src)}</p>
    </ResultLinkOut>
  );
}

function VideoResult({ r, i }) {
  const url = r.content || r.url;
  const img = r.images?.large || r.images?.medium || r.images?.small || r.thumbnail;
  return (
    <ResultLinkOut href={url} title={r.title} className="block group" key={i}>
      <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border border-[var(--omni-border)] relative" data-testid={`video-result-${i}`}>
        {img && <img src={img} alt={r.title || ""} className="w-full h-full object-cover" loading="lazy" />}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition">
          <PlayCircle className="text-white opacity-90" size={56} />
        </div>
      </div>
      <p className="text-sm font-medium mt-2 line-clamp-2">{r.title}</p>
      <p className="text-xs text-gray-500">{r.publisher || getHost(url)} {r.duration ? `· ${r.duration}` : ""}</p>
    </ResultLinkOut>
  );
}

function NewsResult({ r, i }) {
  const url = r.url;
  return (
    <li className="omni-fade-in" style={{ animationDelay: `${i * 30}ms` }} data-testid={`news-result-${i}`}>
      <ResultLinkOut href={url} title={r.title} className="flex gap-4 items-start">
        {r.image && (
          <img src={r.image} alt="" className="w-32 h-24 object-cover rounded-lg border border-[var(--omni-border)] flex-shrink-0" loading="lazy" />
        )}
        <div>
          <div className="text-xs text-gray-500">{r.source || getHost(url)} {r.date ? `· ${r.date}` : ""}</div>
          <h3 className="result-title text-lg font-medium mt-0.5 text-[#1a0dab] leading-snug">{r.title}</h3>
          <p className="text-sm text-gray-700 mt-1">{r.body}</p>
        </div>
      </ResultLinkOut>
    </li>
  );
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const type = params.get("type") || "web";
  usePageTitle(q ? `${q} — Recherche` : "Recherche");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!q) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResults([]);
    setAiSummary(null);
    setPage(1);
    setHasMore(true);
    api
      .get(`/search`, { params: { q, type, page: 1 } })
      .then(({ data }) => {
        if (cancelled) return;
        const rs = data.results || [];
        setResults(rs);
        if (rs.length === 0) setHasMore(false);
        if (type === "web" && rs.length > 0) fetchAi(q, rs);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, type]);

  // Infinite scroll for images
  useEffect(() => {
    if (type !== "images" || !q || loading || loadingMore || !hasMore) return;
    const onScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 800;
      if (nearBottom) loadMore();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, q, loading, loadingMore, hasMore, page]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const { data } = await api.get(`/search`, { params: { q, type, page: nextPage } });
      const rs = data.results || [];
      if (rs.length === 0) { setHasMore(false); return; }
      // Dedupe by url/image
      setResults((prev) => {
        const seen = new Set(prev.map((x) => x.url || x.image || x.href));
        const fresh = rs.filter((x) => !seen.has(x.url || x.image || x.href));
        if (fresh.length === 0) setHasMore(false);
        return [...prev, ...fresh];
      });
      setPage(nextPage);
    } catch {} finally { setLoadingMore(false); }
  };

  const fetchAi = async (query, rs) => {
    setAiLoading(true);
    try {
      const snippets = rs.slice(0, 6).map((r) => `${r.title || ""}: ${r.body || r.description || ""}`);
      const { data } = await api.post("/ai/summary", { query, snippets });
      setAiSummary(data.summary);
    } catch {} finally { setAiLoading(false); }
  };

  const setTab = (t) => setParams({ q, type: t });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Search header */}
      <header className="border-b border-[var(--omni-border)] sticky top-0 bg-white z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4 sm:gap-6">
          <Logo size="sm" />
          <div className="flex-1 max-w-2xl">
            <SearchBar initial={q} size="sm" />
          </div>
          <AppsMenu />
          <MainMenu />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center gap-6 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className="omni-tab inline-flex items-center gap-2 text-sm"
              data-active={type === id}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-6">
        {!q && (
          <div className="text-center py-20 text-gray-500">Tapez votre recherche pour commencer.</div>
        )}

        {q && loading && (
          <div className="flex items-center gap-2 text-gray-500 py-12">
            <Loader2 className="animate-spin" size={18} /> Recherche en cours…
          </div>
        )}

        {error && <div className="text-red-600 py-6" data-testid="search-error">{error}</div>}

        {q && !loading && type === "web" && (
          <>
            {(aiLoading || aiSummary) && (
              <div className="mb-6 p-5 rounded-lg border border-[var(--omni-border)] bg-[var(--omni-surface)] omni-fade-in" data-testid="ai-summary">
                <div className="flex items-center gap-2 text-sm font-bold mb-2">
                  <Sparkles size={16} className="text-[var(--omni-accent)]" />
                  Résumé IA <span className="text-[10px] font-mono text-gray-500 ml-1">Claude Sonnet 4.5</span>
                </div>
                {aiLoading ? (
                  <div className="text-sm text-gray-500 inline-flex items-center gap-2">
                    <Loader2 className="animate-spin" size={14} /> Synthèse…
                  </div>
                ) : (
                  <p className="text-sm leading-relaxed text-gray-800">{aiSummary}</p>
                )}
              </div>
            )}
            <div className="text-xs text-gray-500 mb-3">Environ {results.length} résultats</div>
            <ul className="space-y-7">
              {results.map((r, i) => <WebResult key={i} r={r} i={i} />)}
            </ul>
          </>
        )}

        {q && !loading && type === "images" && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {results.map((r, i) => <ImageResult key={`${r.url || r.image}-${i}`} r={r} i={i} />)}
            </div>
            {loadingMore && (
              <div className="flex items-center justify-center py-8 text-gray-500" data-testid="images-loading-more">
                <Loader2 className="animate-spin mr-2" size={16} /> Plus d'images…
              </div>
            )}
            {!hasMore && results.length > 0 && (
              <div className="text-center text-xs text-gray-400 py-8">Fin des résultats · {results.length} images</div>
            )}
          </>
        )}

        {q && !loading && type === "videos" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.map((r, i) => <VideoResult key={i} r={r} i={i} />)}
          </div>
        )}

        {q && !loading && type === "news" && (
          <ul className="space-y-6">
            {results.map((r, i) => <NewsResult key={i} r={r} i={i} />)}
          </ul>
        )}

        {q && !loading && results.length === 0 && !error && (
          <div className="py-16 text-center text-gray-500">Aucun résultat. Essayez d'autres mots-clés.</div>
        )}
      </main>

      <footer className="border-t border-[var(--omni-border)] py-4 text-xs text-gray-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <span>Résultats fournis par DuckDuckGo · Synthèse IA par Claude</span>
          <Link to="/about" className="hover:text-black" data-testid="results-footer-about">À propos</Link>
        </div>
      </footer>
    </div>
  );
}
